import type { Vector2, Vector3, Bounds } from '@kitware/vtk.js/types';
import { mat3, quat } from 'gl-matrix';

export interface ArchSample {
  point: Vector3;
  tangent: Vector3;
  distance: number;
}

/** Fit an open dental arch through posterior, anterior, posterior landmarks. */
export function archFromLandmarks(landmarks: Vector2[]): Vector2[] | null {
  if (landmarks.length !== 3) return null;
  const [a, middle, b] = landmarks;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const chord = Math.hypot(dx, dy);
  if (chord < 5) return null;
  const along =
    ((middle[0] - a[0]) * dx + (middle[1] - a[1]) * dy) / (chord * chord);
  const depth =
    Math.abs(dx * (middle[1] - a[1]) - dy * (middle[0] - a[0])) / chord;
  if (along < 0.1 || along > 0.9 || depth < chord * 0.15) return null;
  const control = [
    2 * middle[0] - (a[0] + b[0]) / 2,
    2 * middle[1] - (a[1] + b[1]) / 2,
  ];
  return Array.from({ length: 13 }, (_, i) => {
    const t = i / 12;
    return [
      (1 - t) ** 2 * a[0] + 2 * t * (1 - t) * control[0] + t * t * b[0],
      (1 - t) ** 2 * a[1] + 2 * t * (1 - t) * control[1] + t * t * b[1],
    ];
  });
}

export function isCompleteArch(
  points: Vector2[],
  samples: ArchSample[],
  bounds: Bounds
) {
  if (points.length < 5 || samples.length < 2) return false;
  const first = points[0];
  const last = points.at(-1)!;
  const chord = Math.hypot(last[0] - first[0], last[1] - first[1]);
  const minimumSpan = Math.min(
    30,
    Math.max(bounds[1] - bounds[0], bounds[3] - bounds[2]) * 0.25
  );
  return chord >= minimumSpan && samples.at(-1)!.distance > chord * 1.1;
}

// Interpolate in patient LPS coordinates, never voxel indices. Limit each
// segment to its endpoints' box to avoid spline overshoot outside the volume.
export function sampleArch(
  points: Vector2[],
  z: number,
  step = 0.5
): ArchSample[] {
  const unique = points.filter(
    (p, i) =>
      !i || Math.hypot(p[0] - points[i - 1][0], p[1] - points[i - 1][1]) > 0.01
  );
  if (unique.length < 2) return [];
  const result: Vector3[] = [];
  for (let i = 0; i < unique.length - 1; i++) {
    const a = unique[Math.max(0, i - 1)];
    const b = unique[i];
    const c = unique[i + 1];
    const d = unique[Math.min(unique.length - 1, i + 2)];
    const count = Math.max(
      2,
      Math.ceil(Math.hypot(c[0] - b[0], c[1] - b[1]) / Math.max(0.1, step))
    );
    for (let j = 0; j < count; j++) {
      const t = j / count;
      const xy = [0, 1].map((axis) => {
        const v =
          0.5 *
          (2 * b[axis] +
            (-a[axis] + c[axis]) * t +
            (2 * a[axis] - 5 * b[axis] + 4 * c[axis] - d[axis]) * t * t +
            (-a[axis] + 3 * b[axis] - 3 * c[axis] + d[axis]) * t * t * t);
        return Math.max(
          Math.min(b[axis], c[axis]),
          Math.min(Math.max(b[axis], c[axis]), v)
        );
      });
      if (
        !result.length ||
        Math.hypot(xy[0] - result.at(-1)![0], xy[1] - result.at(-1)![1]) > 0.001
      )
        result.push([xy[0], xy[1], z]);
    }
  }
  result.push([...unique.at(-1)!, z]);
  let distance = 0;
  return result.map((point, i) => {
    if (i)
      distance += Math.hypot(
        point[0] - result[i - 1][0],
        point[1] - result[i - 1][1]
      );
    const before = result[Math.max(0, i - 1)];
    const after = result[Math.min(result.length - 1, i + 1)];
    const dx = after[0] - before[0];
    const dy = after[1] - before[1];
    const length = Math.hypot(dx, dy) || 1;
    return { point, tangent: [dx / length, dy / length, 0], distance };
  });
}

export function archFrame(tangent: Vector3) {
  // CPR local X samples superior/inferior; local Y projects through the
  // slab across the arch; local Z follows the curve. Right-handed basis.
  const [tx, ty] = tangent;
  return quat.fromMat3(
    quat.create(),
    mat3.fromValues(0, 0, 1, ty, -tx, 0, tx, ty, 0)
  );
}

export function locateOnArch(
  samples: ArchSample[],
  fraction: number
): ArchSample | null {
  if (samples.length < 2) return null;
  const distance =
    Math.max(0, Math.min(1, fraction)) * samples.at(-1)!.distance;
  const i = Math.max(
    1,
    samples.findIndex((s) => s.distance >= distance)
  );
  const a = samples[i - 1];
  const b = samples[i];
  const t = (distance - a.distance) / (b.distance - a.distance || 1);
  const dx = b.point[0] - a.point[0];
  const dy = b.point[1] - a.point[1];
  const norm = Math.hypot(dx, dy) || 1;
  return {
    point: [a.point[0] + t * dx, a.point[1] + t * dy, a.point[2]],
    tangent: [dx / norm, dy / norm, 0],
    distance,
  };
}

export function clampToAxialBounds(point: Vector2, bounds: Bounds): Vector2 {
  return [
    Math.max(bounds[0], Math.min(bounds[1], point[0])),
    Math.max(bounds[2], Math.min(bounds[3], point[1])),
  ];
}
